import CloseIcon from "hec/assets/icons/drawer-close-icon";
import type React from "react";

import {
  SET_CIO_SELECTED_FACETS,
  useCioFacetSelector,
} from "~/hec/context/cio-facet-adapter";
import { cn } from "~/lib/ui";

interface FacetOption {
  value: string;
  display_name: string;
}

interface SelectedFacetFilters {
  [category: string]: FacetOption[];
}

interface FilterTagsProps {
  "data-testid"?: string;
  className?: string;
}

function isEmptyObject(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length === 0;
}

export const FilterTags: React.FC<FilterTagsProps> = ({
  "data-testid": dataTestId,
  className,
}) => {
  const selectedFacetFilters = useCioFacetSelector(
    context => context?.state.selectedCioFacets,
  ) as SelectedFacetFilters;
  const dispatchFacetFilters = useCioFacetSelector(
    context => context?.dispatch,
  );

  const handleSelectedFilter = (category: string, value: string) => {
    const newSelectedFilters = { ...selectedFacetFilters };
    newSelectedFilters[category] = newSelectedFilters[category].filter(
      item => item.value !== value,
    );
    if (newSelectedFilters[category].length === 0) {
      delete newSelectedFilters[category];
    }
    dispatchFacetFilters({
      type: SET_CIO_SELECTED_FACETS,
      payload: newSelectedFilters,
    });
  };

  if (isEmptyObject(selectedFacetFilters)) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {Object.entries(selectedFacetFilters).map(([category, values]) =>
        values.map(item => {
          const label = item.display_name.startsWith("-inf")
            ? item.display_name.replace("-inf -", "").trim() + " and less"
            : item.display_name.startsWith("0")
              ? item.display_name.replace("0 -", "").trim() + " and less"
              : item.display_name.endsWith("inf")
                ? item.display_name.replace("- inf", "").trim() + " and more"
                : item.display_name.endsWith("0")
                  ? item.display_name.replace("- 0", "").trim() + " and more"
                  : item.display_name;

          return (
            <button
              key={`${category}-${item.value}`}
              onClick={() => handleSelectedFilter(category, item.value)}
              className="flex cursor-pointer items-center gap-2 rounded-lg bg-gray-01 py-2 pl-3.5 pr-2 font-grotesque text-sm font-light text-primary"
              data-testid={dataTestId || "filterTags"}
            >
              <span>{label}</span>
              <CloseIcon
                className="h-3 shrink-0 fill-accent"
                data-testid={dataTestId || "filterTags-CloseIcon"}
              />
              <span className="sr-only">Remove {item.display_name}</span>
            </button>
          );
        }),
      )}
      {/* Clear All Button (Only show if there are tags) */}
      <button
        onClick={() =>
          dispatchFacetFilters({
            type: SET_CIO_SELECTED_FACETS,
            payload: {},
          })
        }
        className="cursor-pointer font-grotesque text-sm font-medium text-primary underline"
      >
        Clear all
      </button>
    </div>
  );
};
